import { useState } from "react";
import { useInView } from "react-intersection-observer";
import { useNavigate } from "react-router-dom-v5-compat";
import {
  type ClaimOfferError,
  type ClaimResult,
  useAdTracking,
  useClaimOffer,
} from "@b2bportal/core-media";
import { IconNameEnum } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Icon } from "@tenant/ui";
import type { AdWithOfferTemplate } from "../hooks/useHtsMediaDealTiles";
import {
  claimDialogContent,
  HtsMediaClaimDialog,
} from "../HtsMediaClaimDialog";
import { getOfferHotelSearchPath } from "../offerHotelSearch";
import { ClaimedOfferTile } from "./ClaimedOfferTile";
import { getOfferTileTrackingProperties } from "./offerTileTracking";
import { DealTile } from "@/modules/dealTiles";
import { getOfferImageSrc } from "@/modules/hotel-funded-offers/offerImage";
import {
  type OfferScreen,
  IsOfferClaimed,
  OfferRawEventName,
  useEmitRbcRawEvent,
  useEmitRbcRawEventOnce,
} from "@/tracking/offers";
import {
  getClaimFailureReason,
  getClaimStatusFromError,
  getClaimStatusFromSuccess,
} from "@/tracking/offers/claimStatusMapping";

export interface HtsMediaDealTileProps {
  ad: AdWithOfferTemplate;
  /** Position of this tile in its section (0-based). */
  tileIndex: number;
  screen: OfferScreen;
  className?: string;
}

/**
 * Sponsored deal tile (Figma 39784-23410): "Load" claims the offer and opens the
 * confirmation dialog; once dismissed the tile switches to its loaded state in place.
 */
export const HtsMediaDealTile = ({
  ad,
  tileIndex,
  screen,
  className,
}: HtsMediaDealTileProps) => {
  const { t } = useI18nContext();
  const navigate = useNavigate();
  const [claimResult, setClaimResult] = useState<ClaimResult>();
  const [dialogOpen, setDialogOpen] = useState(false);

  const emitTappedTile = useEmitRbcRawEvent(OfferRawEventName.TappedOfferTile);

  const emitTappedWithClaimResult = (result: ClaimResult) => {
    const claimStatus = getClaimStatusFromSuccess(result);
    emitTappedTile({
      properties: {
        ...baseTrackingProperties,
        claim_status: claimStatus,
      },
    });
  };

  const emitTappedWithClaimError = (error: ClaimOfferError) => {
    const claimStatus = getClaimStatusFromError(error);
    emitTappedTile({
      properties: {
        ...baseTrackingProperties,
        claim_status: claimStatus,
        claim_failure_reason: getClaimFailureReason(claimStatus, error),
      },
    });
  };

  // HTS insertion/impression events; click is tracked by `useClaimOffer`.
  const { adRef } = useAdTracking<HTMLDivElement>(ad);
  // Amplitude view event, same viewability rule as the funded tile.
  const { ref: inViewRef, inView } = useInView({
    threshold: [0.5],
    delay: 1000,
    triggerOnce: true,
  });
  const setRefs = (node: HTMLDivElement | null) => {
    adRef.current = node;
    inViewRef(node);
  };
  const { claimOffer, claiming, claimError } = useClaimOffer({
    onSuccess: (result) => {
      setClaimResult(result);
      setDialogOpen(true);
      emitTappedWithClaimResult(result);
    },
    onError: (error) => {
      emitTappedWithClaimError(error);
    },
  });

  const { creative, offerTemplate } = ad;
  const tileBaseProperties = getOfferTileTrackingProperties({
    offer: offerTemplate,
    creative,
    countryName: ad.fullyQualifiedDestination?.countryName,
    tileIndex,
  });

  const baseTrackingProperties = {
    ...tileBaseProperties,
    screen,
    is_claimable: true,
  };

  useEmitRbcRawEventOnce(
    {
      name: OfferRawEventName.ViewedOfferTile,
      properties: {
        ...baseTrackingProperties,
        is_offer_claimed: IsOfferClaimed.false,
      },
    },
    inView
  );

  const handleLoad = () => {
    void claimOffer(ad);
  };

  const content =
    claimResult != null ? claimDialogContent(ad, claimResult) : undefined;
  const imageSrc = getOfferImageSrc(creative.imageUrl);

  if (content != null && !dialogOpen) {
    return (
      <ClaimedOfferTile
        rootRef={setRefs}
        className={className}
        content={content}
        imageSrc={imageSrc}
        description={creative.bodyText}
        trackingProperties={tileBaseProperties}
        screen={screen}
      />
    );
  }

  return (
    <>
      <DealTile
        rootRef={setRefs}
        className={className}
        imageSrc={imageSrc}
        imageAlt={creative.destinationName ?? ""}
        title={creative.titleText}
        description={creative.bodyText}
        expiry={offerTemplate.expiry}
        action={{
          label: t("dealTile.load"),
          icon: <Icon iconName={IconNameEnum.plusCircle} aria-hidden />,
          onClick: handleLoad,
          disabled: claiming,
        }}
        error={claimError ? t("htsMedia.banner.claimError") : undefined}
      />
      <HtsMediaClaimDialog
        open={dialogOpen}
        content={content}
        ctaLabel={t("htsMedia.dialog.cta")}
        onCtaClick={() => navigate(getOfferHotelSearchPath(offerTemplate))}
        onClose={() => setDialogOpen(false)}
        screen={screen}
      />
    </>
  );
};
